export enum Interval {
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  TWO_QUARTERLY = 'TWO_QUARTERLY',
  THREE_QUARTERLY = 'THREE_QUARTERLY',
  YEARLY = 'YEARLY',
}

export interface Subscription {
  id: number;
  customer_id?: string;
  plan: Plan;
  scheduled: ScheduledPlan;
  status: 'ACTIVE' | 'INACTIVE';
  is_cancelled: boolean;
  disable_cancel: boolean;
  interval: Interval;
  current_period: {
    start: Date;
    end: Date;
  };
  price: number;
  trial_ends: Date;
  is_trial: boolean;
  payment_method: 'CREDIT_CARD' | 'WIRE_TRANSFER';
  current_live_vacs: number;
  max_live_vacs: number;
}

export interface Plan {
  name: string;
  tier: string;
  prices?: PlanPrice[];
  features?: string[];
}

export const SUBSCRIPTION_CAPABILITIES = {
  PAY_TRANSPARENCY_BASE: 'pay_transparency_base',
  PAY_TRANSPARENCY_MANDATORY_TOGGLE: 'pay_transparency_mandatory_toggle',
  /**
   * SYM-6051. WHETHER this customer has the new ad templates at all.
   *
   * The server resolves it from the same feature flag the browser used to read on its own, and
   * hands the answer down here. Two readers of one flag could disagree -- the picker offering a
   * design the server then refused to draw -- and now there is one.
   */
  AD_TEMPLATES_V2: 'ad_templates_v2',
} as const;

/**
 * SYM-6051. WHICH ad templates the plan gets, once the customer has them at all.
 *
 * A separate question from the capability above, deliberately: the flag is the rollout valve and
 * the plan is the packaging. `starter` is the small set the cheapest plans get.
 */
export const AD_TEMPLATE_LEVELS = {
  STARTER: 'starter',
  FULL: 'full',
} as const;

export type AdTemplateLevel =
  (typeof AD_TEMPLATE_LEVELS)[keyof typeof AD_TEMPLATE_LEVELS];

export type SubscriptionCapabilityKey =
  (typeof SUBSCRIPTION_CAPABILITIES)[keyof typeof SUBSCRIPTION_CAPABILITIES];

export type SubscriptionCapabilities = Partial<
  Record<SubscriptionCapabilityKey, boolean>
> &
  Record<string, boolean | undefined>;

export interface SubscriptionConfiguration {
  capabilities?: SubscriptionCapabilities;
  /**
   * Absent only if the server is older than SYM-6051; it always sends the key otherwise, precisely
   * so nothing here has to decide what a missing answer means. Read it through
   * `adTemplateLevel` on the navigation context rather than directly, which resolves the
   * absent case to `full` in one place.
   */
  ad_template_level?: AdTemplateLevel;
}

interface ScheduledPlan {
  interval: Interval;
  amount: number;
  plan: {
    tier: string;
    name: string;
  };
}

interface PlanPrice {
  interval: Interval;
  amount: number;
  id: number;
}
